import React, { useContext, useState, useEffect } from 'react';
import AuthContext from '../../context/AuthContext';
import ThemeContext from '../../context/ThemeContext';
import axios from 'axios';
import {
    Box,
    Button,
    Container,
    Heading,
    Text,
    VStack,
    HStack,
    Icon,
    Card,
    CardBody,
    Flex,
    Spinner,
    Alert,
    AlertIcon,
    AlertTitle,
    AlertDescription,
    Badge,
    useBreakpointValue,
    SimpleGrid,
    Stat,
    StatLabel,
    StatNumber,
    Tag,
    Wrap,
    WrapItem,
    useToast,
    CircularProgress,
    CircularProgressLabel,
    Input,
    InputGroup,
    InputLeftElement,
    IconButton,
    Collapse,
} from '@chakra-ui/react';
import {
    FaChartLine,
    FaSync,
    FaUsers,
    FaInfoCircle,
    FaLightbulb,
    FaBrain,
    FaSearch,
} from 'react-icons/fa';
import { ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';

const isProduction = process.env.NODE_ENV === 'production';
const API_BASE_URL = isProduction
    ? process.env.REACT_APP_API_BASE_URL_PROD
    : process.env.REACT_APP_API_BASE_URL_DEV;

function DashboardRMPriorityScores() {
    const { token } = useContext(AuthContext);
    const { themeData } = useContext(ThemeContext);
    const [priorityData, setPriorityData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [expandedCustomer, setExpandedCustomer] = useState(null);
    const toast = useToast();

    const statSize = useBreakpointValue({ base: 'sm', md: 'md' });

    // Extract theme colors with fallbacks
    const primaryColor = themeData?.colors?.primary || '#2B6CB0';
    const secondaryColor = themeData?.colors?.secondary || '#2C5282';
    const accentColor = themeData?.colors?.accent || '#63B3ED';
    const backgroundColor = themeData?.colors?.background || '#F7FAFC';

    // Determine if theme is dark (midnight)
    const isDarkTheme = themeData?.id === 'midnight';

    // Card background - improved for dark theme
    const cardBg = isDarkTheme ? 'rgba(45, 55, 72, 0.6)' : 'white';

    // Text colors with proper contrast - following Dashboard.js pattern
    const headingTextColor = isDarkTheme ? '#F7FAFC' : primaryColor;
    const bodyTextColor = isDarkTheme ? '#E2E8F0' : 'gray.600';
    const mutedTextColor = isDarkTheme ? '#CBD5E0' : 'gray.500';
    const labelTextColor = isDarkTheme ? '#E2E8F0' : 'gray.700';
    const strongTextColor = isDarkTheme ? '#F7FAFC' : 'gray.800';

    // Border colors
    const borderColor = isDarkTheme ? 'rgba(66, 153, 225, 0.3)' : 'gray.200';
    const lightBorderColor = isDarkTheme ? 'rgba(66, 153, 225, 0.2)' : 'gray.100';

    // Info card backgrounds - improved transparency for dark theme
    const actionBg = isDarkTheme ? 'rgba(99, 179, 237, 0.15)' : 'blue.50';
    const biasBg = isDarkTheme ? 'rgba(66, 153, 225, 0.15)' : accentColor + '10';

    // Placeholder/empty background
    const placeholderBg = isDarkTheme ? 'rgba(45, 55, 72, 0.4)' : 'gray.50';

    // Button hover
    const buttonHoverBg = isDarkTheme ? accentColor : secondaryColor;

    // Icon colors
    const mutedIconColor = isDarkTheme ? mutedTextColor : 'gray.400';
    const infoIconColor = isDarkTheme ? bodyTextColor : 'gray.600';

    const fetchPriorityScores = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await axios.get(`${API_BASE_URL}/content/rm-priority-scores`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setPriorityData(Array.isArray(response.data) ? response.data : []);
        } catch (err) {
            console.error('Error fetching priority scores:', err);
            const msg = err.response?.data?.message || 'Failed to fetch priority scores';
            setError(msg);
            toast({
                title: 'Error',
                description: msg,
                status: 'error',
                duration: 4000,
                isClosable: true,
                position: 'top',
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (token) fetchPriorityScores();
        // eslint-disable-next-line
    }, [token]);

    const getUrgencyColor = (urgencyLevel) => {
        const level = urgencyLevel?.toLowerCase();
        if (level === 'urgent' || level === 'high') return 'red';
        if (level === 'medium' || level === 'moderate') return 'orange';
        return 'green';
    };

    const getPriorityColor = (score) => {
        if (score >= 8) return 'red';
        if (score >= 6) return 'orange';
        if (score >= 4) return 'yellow';
        return 'green';
    };

    // Toggle customer details
    const toggleCustomer = (customerId) => {
        if (expandedCustomer === customerId) {
            setExpandedCustomer(null);
        } else {
            setExpandedCustomer(customerId);
        }
    };

    // Handle search
    const handleSearch = () => {
        // Search is handled by filteredPriorityData already
    };

    // Stats
    const avgPriority =
        priorityData.length > 0
            ? (priorityData.reduce((sum, c) => sum + c.priority_score, 0) / priorityData.length).toFixed(2)
            : 0;

    const highUrgencyCount = priorityData.filter(
        (c) => c.urgency_level?.toLowerCase() === 'high' || c.urgency_level?.toLowerCase() === 'urgent'
    ).length;

    const filteredPriorityData = priorityData.filter(customer =>
        customer.customer_id.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <Box minH="100vh" width="100%" pb={{ base: '90px', md: '20px' }} bg={backgroundColor}>
            <Container maxW="container.xl" py={{ base: 4, md: 8 }} px={{ base: 3, md: 6 }}>
                <VStack spacing={{ base: 4, md: 6 }} align="stretch">
                    <Box textAlign="center" pt={{ base: 2, md: 4 }}>
                        <Heading
                            size={{ base: 'lg', md: 'xl' }}
                            bgGradient={isDarkTheme 
                                ? `linear(to-r, ${accentColor}, ${primaryColor})` 
                                : `linear(to-r, ${primaryColor}, ${accentColor})`
                            }
                            bgClip="text"
                            mb={2}
                            fontWeight="extrabold"
                        >
                            <HStack justify="center" spacing={2}>
                                <Icon as={FaBrain} boxSize={{ base: 6, md: 8 }} />
                                <Text>Customer Priority Scores</Text>
                            </HStack>
                        </Heading>
                        <Text color={bodyTextColor} fontSize={{ base: 'sm', md: 'md' }}>
                            AI-driven insights ranking customers by behavioral and risk factors
                        </Text>
                    </Box>

                    {/* Summary Cards */}
                    {!loading && (
                        <SimpleGrid columns={{ base: 3, md: 3 }} spacing={{ base: 3, md: 4 }}>
                            {[
                                { icon: FaUsers, label: 'Total Customers', value: priorityData.length, color: isDarkTheme ? accentColor : primaryColor },
                                { icon: FaChartLine, label: 'Avg Priority', value: avgPriority, color: isDarkTheme ? accentColor : accentColor },
                                { icon: FaInfoCircle, label: 'High Urgency', value: highUrgencyCount, color: 'orange.500' },
                            ].map((stat, i) => (
                                <Card 
                                    key={i} 
                                    bg={cardBg} 
                                    borderRadius="xl" 
                                    boxShadow="sm" 
                                    border="1px" 
                                    borderColor={lightBorderColor}
                                >
                                    <CardBody p={{ base: 4, md: 5 }}>
                                        <Stat size={statSize}>
                                            <HStack spacing={2} mb={1}>
                                                <Icon as={stat.icon} color={stat.color} boxSize={{ base: 4, md: 5 }} />
                                                <StatLabel fontSize={{ base: 'xs', md: 'sm' }} color={bodyTextColor}>
                                                    {stat.label}
                                                </StatLabel>
                                            </HStack>
                                            <StatNumber fontSize={{ base: '2xl', md: '3xl' }} color={stat.color}>
                                                {stat.value}
                                            </StatNumber>
                                        </Stat>
                                    </CardBody>
                                </Card>
                            ))}
                        </SimpleGrid>
                    )}

                    {error && (
                        <Alert 
                            status="error" 
                            borderRadius="xl" 
                            variant="left-accent" 
                            bg={cardBg}
                            borderWidth="1px"
                            borderColor={borderColor}
                        >
                            <AlertIcon />
                            <Box>
                                <AlertTitle color={headingTextColor}>Error!</AlertTitle>
                                <AlertDescription color={bodyTextColor}>{error}</AlertDescription>
                            </Box>
                        </Alert>
                    )}

                    {/* Search and Refresh */}
                    <Card 
                        bg={cardBg} 
                        borderRadius="lg" 
                        boxShadow="md" 
                        borderWidth="1px" 
                        borderColor={borderColor}
                    >
                        <CardBody>
                            <HStack spacing={4}>
                                <InputGroup flex={1}>
                                    <InputLeftElement pointerEvents="none">
                                        <Icon as={FaSearch} color={mutedIconColor} />
                                    </InputLeftElement>
                                    <Input
                                        placeholder="Search by Customer ID..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                                        borderColor={borderColor}
                                        _focus={{ borderColor: primaryColor, boxShadow: `0 0 0 1px ${primaryColor}` }}
                                        bg={isDarkTheme ? 'rgba(45, 55, 72, 0.8)' : cardBg}
                                        color={bodyTextColor}
                                    />
                                </InputGroup>
                                <Button
                                    leftIcon={<FaSync />}
                                    bg={primaryColor}
                                    color="white"
                                    onClick={fetchPriorityScores}
                                    isLoading={loading}
                                    _hover={{ bg: buttonHoverBg }}
                                >
                                    Refresh
                                </Button>
                            </HStack>
                        </CardBody>
                    </Card>

                    {/* Customer List */}
                    {loading ? (
                        <Flex justify="center" align="center" py={20}>
                            <VStack spacing={4}>
                                <Spinner size="xl" color={primaryColor} thickness="4px" />
                                <Text color={mutedTextColor} fontSize="sm">Loading priority scores...</Text>
                            </VStack>
                        </Flex>
                    ) : filteredPriorityData.length === 0 ? (
                        <Card 
                            bg={cardBg} 
                            borderRadius="lg" 
                            boxShadow="md" 
                            borderWidth="1px" 
                            borderColor={borderColor}
                        >
                            <CardBody>
                                <Box textAlign="center" py={10}>
                                    <Icon as={searchTerm ? FaSearch : FaChartLine} boxSize={{ base: 10, md: 12 }} color={mutedIconColor} mb={4} />
                                    <Text color={mutedTextColor} fontSize={{ base: 'md', md: 'lg' }} fontWeight="medium">
                                        {searchTerm ? `No customers found matching "${searchTerm}"` : 'No priority scores available'}
                                    </Text>
                                    {searchTerm && (
                                        <Button
                                            mt={4}
                                            variant="link"
                                            color={isDarkTheme ? accentColor : primaryColor}
                                            onClick={() => setSearchTerm('')}
                                        >
                                            Clear search
                                        </Button>
                                    )}
                                </Box>
                            </CardBody>
                        </Card>
                    ) : (
                        <VStack spacing={4} align="stretch">
                            {filteredPriorityData.map((customer, index) => (
                                <Card
                                    key={customer.customer_id || index}
                                    bg={cardBg}
                                    borderRadius="lg"
                                    boxShadow="md"
                                    transition="all 0.2s"
                                    _hover={{ boxShadow: 'lg' }}
                                    borderWidth="1px"
                                    borderColor={borderColor}
                                >
                                    <CardBody>
                                        <Flex justify="space-between" align="center">
                                            <VStack align="start" spacing={2} flex={1}>
                                                <HStack spacing={3} flexWrap="wrap">
                                                    <Heading size="sm" color={headingTextColor}>
                                                        {customer.customer_id}
                                                    </Heading>
                                                    <Badge
                                                        colorScheme={getUrgencyColor(customer.urgency_level)}
                                                        px={3}
                                                        py={1}
                                                        borderRadius="full"
                                                        fontSize="xs"
                                                        fontWeight="bold"
                                                    >
                                                        {customer.urgency_level}
                                                    </Badge>
                                                </HStack>
                                                <HStack spacing={3} align="center">
                                                    <CircularProgress
                                                        value={(customer.priority_score / 10) * 100}
                                                        color={`${getPriorityColor(customer.priority_score)}.400`}
                                                        size="50px"
                                                        thickness="8px"
                                                    >
                                                        <CircularProgressLabel fontSize="xs" fontWeight="bold" color={strongTextColor}>
                                                            {customer.priority_score.toFixed(1)}
                                                        </CircularProgressLabel>
                                                    </CircularProgress>
                                                    <VStack align="start" spacing={0}>
                                                        <Text fontSize="xs" color={mutedTextColor} fontWeight="medium">
                                                            Priority Score
                                                        </Text>
                                                        <Text fontSize="xs" color={mutedTextColor}>
                                                            Max: 10.0
                                                        </Text>
                                                    </VStack>
                                                </HStack>
                                            </VStack>
                                            <IconButton
                                                icon={
                                                    expandedCustomer === customer.customer_id ? (
                                                        <ChevronUpIcon />
                                                    ) : (
                                                        <ChevronDownIcon />
                                                    )
                                                }
                                                onClick={() => toggleCustomer(customer.customer_id)}
                                                colorScheme="blue"
                                                variant="ghost"
                                                aria-label="Toggle details"
                                            />
                                        </Flex>

                                        <Collapse in={expandedCustomer === customer.customer_id} animateOpacity>
                                            <Box mt={6} pt={6} borderTop="1px solid" borderColor={borderColor}>
                                                <VStack align="stretch" spacing={4}>
                                                    {/* Recommended Action */}
                                                    {customer.recommended_action && (
                                                        <Card 
                                                            bg={actionBg} 
                                                            borderRadius="md" 
                                                            boxShadow="sm" 
                                                            borderWidth="1px" 
                                                            borderColor={borderColor}
                                                        >
                                                            <CardBody>
                                                                <HStack spacing={2} mb={2}>
                                                                    <Icon as={FaLightbulb} color={isDarkTheme ? accentColor : accentColor} boxSize={5} />
                                                                    <Text fontWeight="bold" color={labelTextColor}>Recommended Action</Text>
                                                                </HStack>
                                                                <Text fontSize="sm" color={labelTextColor} lineHeight="1.6">
                                                                    {customer.recommended_action}
                                                                </Text>
                                                            </CardBody>
                                                        </Card>
                                                    )}

                                                    {/* Top Biases */}
                                                    {customer.top_biases?.length > 0 && (
                                                        <Card 
                                                            bg={biasBg} 
                                                            borderRadius="md" 
                                                            boxShadow="sm" 
                                                            borderWidth="1px" 
                                                            borderColor={borderColor}
                                                        >
                                                            <CardBody>
                                                                <HStack spacing={2} mb={3}>
                                                                    <Icon as={FaBrain} color={isDarkTheme ? accentColor : primaryColor} boxSize={5} />
                                                                    <Text fontWeight="bold" color={labelTextColor}>Key Behavioral Biases</Text>
                                                                </HStack>
                                                                <Wrap spacing={2}>
                                                                    {customer.top_biases.map((bias, i) => (
                                                                        <WrapItem key={i}>
                                                                            <Tag colorScheme="purple" size="md" variant="subtle" borderRadius="full">
                                                                                {bias.bias_name || 'N/A'}
                                                                            </Tag>
                                                                        </WrapItem>
                                                                    ))}
                                                                </Wrap>
                                                            </CardBody>
                                                        </Card>
                                                    )}

                                                    {/* Breakdown Details */}
                                                    {customer.breakdown && (
                                                        <Card 
                                                            bg={placeholderBg} 
                                                            borderRadius="md" 
                                                            boxShadow="sm" 
                                                            borderWidth="1px" 
                                                            borderColor={borderColor}
                                                        >
                                                            <CardBody>
                                                                <HStack spacing={2} mb={3}>
                                                                    <Icon as={FaInfoCircle} color={infoIconColor} boxSize={5} />
                                                                    <Text fontWeight="bold" color={labelTextColor}>Score Breakdown</Text>
                                                                </HStack>
                                                                <VStack align="stretch" spacing={2}>
                                                                    {Object.entries(customer.breakdown).map(([key, value]) => (
                                                                        <Flex key={key} justify="space-between" fontSize="sm">
                                                                            <Text textTransform="capitalize" color={bodyTextColor}>
                                                                                {key.replace(/_/g, ' ')}:
                                                                            </Text>
                                                                            <Text fontWeight="semibold" color={strongTextColor}>
                                                                                {typeof value === 'number' ? value.toFixed(2) : value}
                                                                            </Text>
                                                                        </Flex>
                                                                    ))}
                                                                </VStack>
                                                            </CardBody>
                                                        </Card>
                                                    )}
                                                </VStack>
                                            </Box>
                                        </Collapse>
                                    </CardBody>
                                </Card>
                            ))}
                        </VStack>
                    )}

                    {/* Summary footer */}
                    {!loading && filteredPriorityData.length > 0 && (
                        <Card 
                            bg={cardBg} 
                            borderRadius="lg" 
                            boxShadow="md" 
                            borderWidth="1px" 
                            borderColor={borderColor}
                        >
                            <CardBody>
                                <Text fontSize="sm" color={bodyTextColor} textAlign="center">
                                    Showing {filteredPriorityData.length} of {priorityData.length} customers
                                </Text>
                            </CardBody>
                        </Card>
                    )}
                </VStack>
            </Container>
        </Box>
    );
}

export default DashboardRMPriorityScores;